import { Legend } from 'recharts';
import { useTheme } from 'styled-components';
import { Stack, StackItem } from '@optimus-web/core';
import { getDataVizColor, LEGEND_WRAPPER_STYLES, LegendContent } from '../common';
import { ChartState } from '../common/components/ChartState';
import { BaseChartProps } from '../types';
import { ProgressContainer, ProgressValue, ProgressValueInner } from './style';

type ProgressLegendProps =
    | {
          showValues: boolean;
      }
    | boolean;

export interface ProgressChartProps extends Omit<BaseChartProps, 'legend'> {
    total?: number;
    cornerRadius?: boolean;
    legend?: ProgressLegendProps;
}

export const ProgressChart = ({
    cornerRadius = true,
    data,
    total,
    minWidth = '100%',
    title,
    id,
    animated = true,
    legend,
    labelFormatter,
    valueFormatter,
    loading,
    error,
    empty,
}: ProgressChartProps) => {
    const theme = useTheme();
    const displayChartState = loading || error || empty;

    if (displayChartState) {
        return <ChartState empty={empty} error={error} />;
    }

    return (
        <Stack vertical spacing="100">
            {legend ? (
                <StackItem>
                    <Legend
                        payload={data}
                        wrapperStyle={LEGEND_WRAPPER_STYLES}
                        content={
                            <LegendContent
                                payload={data}
                                spacing="150"
                                showValues={typeof legend === 'object' && legend.showValues}
                                valueFormatter={valueFormatter}
                                labelFormatter={labelFormatter}
                            />
                        }
                    />
                </StackItem>
            ) : null}
            <StackItem>
                <ProgressContainer minWidth={minWidth} aria-label={title} role="progressbar" id={id}>
                    {data.map((item, index) => (
                        <ProgressValue
                            value={total ? Math.round((item.value * 100) / total) : item.value}
                            key={item.id}
                            multipleValues={data.length > 1}
                            animation={animated}
                            cornerRadius={cornerRadius}
                        >
                            <ProgressValueInner
                                multipleValues={data.length > 1}
                                progressColor={getDataVizColor(index, item.color)({ theme })}
                                cornerRadius={cornerRadius}
                            />
                        </ProgressValue>
                    ))}
                </ProgressContainer>
            </StackItem>
        </Stack>
    );
};
